import {
  ArrowRight,
  Box,
  Layers,
  Puzzle,
  Sparkles,
  TrendingUp,
  X,
  Zap,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { LoaderFunctionArgs, MetaFunction } from 'react-router';
import { Link, useFetcher, useLoaderData } from 'react-router';
import SpecCard from '~/components/spec/SpecCard';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { getSession } from '~/lib/auth/session';
import {
  getAllFeatureSlugs,
  getAllSpecsWithCounts,
  getTrendingSpecs,
  getUserStarredSet,
  searchSpecs,
} from '~/lib/db/queries/specs';
import { productTemplates } from '~/lib/product-templates';
import { CATEGORY_LABELS, cn } from '~/lib/utils';

export const meta: MetaFunction = () => [
  { title: 'Specly — Software Specs for the AI Era' },
  {
    name: 'description',
    content: 'Browse, compose, and export structured software specifications.',
  },
];

export async function loader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url);
  const category = url.searchParams.get('category') ?? '';
  const q = url.searchParams.get('q') ?? '';
  const sort = (url.searchParams.get('sort') ?? 'newest') as
    | 'newest'
    | 'starred'
    | 'alpha';

  const [specs, allFeatures, session, trendingSpecs] = await Promise.all([
    q ? searchSpecs(q) : getAllSpecsWithCounts(),
    getAllFeatureSlugs(),
    getSession(request),
    getTrendingSpecs(4),
  ]);

  const specIds = specs.map((s) => s.id);
  const starredSet = session
    ? await getUserStarredSet(session.user.id, specIds)
    : new Set<string>();

  const categories = [...new Set(specs.map((s) => s.category))];

  return {
    specs,
    categories,
    activeCategory: category,
    q,
    sort,
    allFeatures,
    starredIds: [...starredSet],
    userId: session?.user.id ?? null,
    trendingSpecs,
  };
}

type SortOption = 'newest' | 'starred' | 'alpha';

const PAGE_SIZE = 12;

export default function Home() {
  const {
    specs,
    categories,
    activeCategory,
    q,
    sort: initialSort,
    allFeatures,
    starredIds,
    userId,
    trendingSpecs,
  } = useLoaderData<typeof loader>();
  const fetcher = useFetcher();

  const [query, setQuery] = useState(q);
  const [sort, setSort] = useState<SortOption>(initialSort);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [localStarred, setLocalStarred] = useState<Set<string>>(
    new Set(starredIds),
  );
  const [aiInput, setAiInput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiMatches, setAiMatches] = useState<
    | {
        slug: string;
        name: string;
        description: string;
        category: string;
        preset: string;
        reason: string;
        url: string;
      }[]
    | null
  >(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const aiFetcher = useFetcher();
  const aiInputRef = useRef<HTMLInputElement>(null);
  const [localStarCounts, setLocalStarCounts] = useState<
    Record<string, number>
  >(() => {
    const map: Record<string, number> = {};
    for (const s of specs) map[s.id] = s.starCount ?? 0;
    return map;
  });

  useEffect(() => {
    setLocalStarred(new Set(starredIds));
  }, [starredIds]);

  // Reset visible count when filters change
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional reset on filter change
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [query, activeCategory, selectedFeatures, sort]);

  useEffect(() => {
    if (aiFetcher.data) {
      const data = aiFetcher.data as {
        matches?: {
          slug: string;
          name: string;
          description: string;
          category: string;
          preset: string;
          reason: string;
          url: string;
        }[];
        error?: string;
      };
      if (data.matches) {
        setAiMatches(data.matches);
        setAiLoading(false);
      } else if (data.error) {
        setAiError(data.error);
        setAiLoading(false);
      }
    }
  }, [aiFetcher.data]);

  function handleAiSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!aiInput.trim()) return;
    setAiLoading(true);
    setAiError(null);
    setAiMatches(null);
    aiFetcher.submit(JSON.stringify({ description: aiInput }), {
      method: 'post',
      action: '/api/ai-suggest',
      encType: 'application/json',
    });
  }

  function clearAiMatches() {
    setAiMatches(null);
    setAiInput('');
  }

  const handleStar = useCallback(
    (specId: string) => {
      if (!userId) {
        window.location.href = '/auth/login';
        return;
      }
      const isCurrentlyStarred = localStarred.has(specId);
      setLocalStarred((prev) => {
        const next = new Set(prev);
        if (isCurrentlyStarred) next.delete(specId);
        else next.add(specId);
        return next;
      });
      setLocalStarCounts((prev) => ({
        ...prev,
        [specId]: (prev[specId] ?? 0) + (isCurrentlyStarred ? -1 : 1),
      }));
      fetcher.submit(JSON.stringify({ specId }), {
        method: 'post',
        action: '/api/stars',
        encType: 'application/json',
      });
    },
    [userId, localStarred, fetcher],
  );

  const toggleFeature = (slug: string) => {
    setSelectedFeatures((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug],
    );
  };

  const filteredSpecs = useMemo(() => {
    let result = specs;
    if (activeCategory)
      result = result.filter((s) => s.category === activeCategory);
    if (query) {
      const lower = query.toLowerCase();
      result = result.filter(
        (s) =>
          s.name.toLowerCase().includes(lower) ||
          s.description.toLowerCase().includes(lower) ||
          s.category.toLowerCase().includes(lower),
      );
    }
    if (selectedFeatures.length > 0) {
      result = result.filter((s) =>
        selectedFeatures.every((slug) => s.featureSlugs?.includes(slug)),
      );
    }
    if (sort === 'starred') {
      result = [...result].sort(
        (a, b) => (localStarCounts[b.id] ?? 0) - (localStarCounts[a.id] ?? 0),
      );
    } else if (sort === 'alpha') {
      result = [...result].sort((a, b) => a.name.localeCompare(b.name));
    } else {
      result = [...result].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
    }
    return result;
  }, [specs, activeCategory, query, selectedFeatures, sort, localStarCounts]);

  const visibleSpecs = filteredSpecs.slice(0, visibleCount);
  const hasMore = filteredSpecs.length > visibleCount;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      {/* Hero */}
      <div className="text-center mb-16 pt-6">
        <div className="inline-flex items-center gap-2 text-xs font-semibold text-primary bg-primary/10 border border-primary/20 rounded-full px-3 py-1 mb-5">
          <Sparkles className="size-3.5" />
          GitHub for specs
        </div>
        <h1 className="text-5xl font-bold mb-4 leading-tight">
          Good specs are the
          <br className="hidden sm:block" /> scarce resource
        </h1>
        <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
          As AI makes code cheap, structured specifications become the moat.
          Browse, compose, and export specs that any AI builder can implement.
        </p>

        {/* AI prompt box */}
        <form onSubmit={handleAiSubmit} className="max-w-xl mx-auto mb-10">
          <div className="flex gap-2">
            <Input
              ref={aiInputRef}
              value={aiInput}
              onChange={(e) => setAiInput(e.target.value)}
              placeholder="I'm building a real-time collaboration tool…"
              className="flex-1 h-11"
              disabled={aiLoading}
            />
            <Button
              type="submit"
              disabled={aiLoading || !aiInput.trim()}
              size="lg"
              className="whitespace-nowrap"
            >
              {aiLoading ? (
                'Thinking…'
              ) : (
                <>
                  Find a spec <ArrowRight data-icon="inline-end" />
                </>
              )}
            </Button>
          </div>
          {aiError && (
            <p className="mt-2 text-sm text-destructive">{aiError}</p>
          )}
          {aiMatches && aiMatches.length > 0 && (
            <div className="mt-4 rounded-xl border border-primary/20 bg-primary/5 p-4 text-left">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-primary">
                  {aiMatches.length === 1 ? 'Best match' : 'Top matches'}
                </p>
                <button
                  type="button"
                  onClick={clearAiMatches}
                  className="p-1 rounded hover:bg-primary/10 text-primary/60 hover:text-primary"
                  aria-label="Clear matches"
                >
                  <X className="size-3.5" />
                </button>
              </div>
              <div className="flex flex-col gap-2">
                {aiMatches.map((m, i) => (
                  <Link
                    key={m.slug}
                    to={m.url}
                    className="flex items-start gap-3 px-3 py-2.5 rounded-lg border border-primary/20 bg-background hover:border-primary hover:bg-primary/5 transition-colors"
                  >
                    <span className="text-xs font-bold text-primary w-5 pt-0.5">
                      #{i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm">{m.name}</span>
                        <span className="text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                          {m.preset}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                        {m.reason}
                      </p>
                    </div>
                    <ArrowRight className="size-4 text-primary/40 flex-shrink-0 mt-1" />
                  </Link>
                ))}
              </div>
            </div>
          )}
        </form>

        {/* Feature callouts */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto mb-4">
          <div className="bg-card rounded-xl ring-1 ring-foreground/10 p-4 text-left shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 rounded-lg bg-primary/10">
                <Puzzle className="size-4 text-primary" />
              </div>
              <span className="text-sm font-semibold">Modular features</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Mix and match reusable feature modules — auth, messaging,
              notifications, and more.
            </p>
          </div>
          <div className="bg-card rounded-xl ring-1 ring-foreground/10 p-4 text-left shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 rounded-lg bg-amber-500/10">
                <Zap className="size-4 text-amber-500" />
              </div>
              <span className="text-sm font-semibold">Decision-first</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Every spec surfaces key tradeoffs. Choose lightweight vs
              enterprise before you write a line.
            </p>
          </div>
          <div className="bg-card rounded-xl ring-1 ring-foreground/10 p-4 text-left shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 rounded-lg bg-green-500/10">
                <Box className="size-4 text-green-500" />
              </div>
              <span className="text-sm font-semibold">Export to AI</span>
            </div>
            <p className="text-xs text-muted-foreground">
              One-click export as a structured prompt for Cursor, Claude, or any
              AI code builder.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-center gap-6 text-sm text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <Layers className="size-4 text-primary/60" />
            Lightweight → enterprise
          </div>
          <span>·</span>
          <div className="flex items-center gap-1.5">
            <Sparkles className="size-4 text-amber-400" />
            AI-ready export
          </div>
        </div>
      </div>

      {/* Product picker */}
      <div className="mb-10">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Start from a real product
          </p>
          <Link
            to="/products"
            className="text-sm text-primary hover:text-primary/80 font-medium inline-flex items-center gap-1"
          >
            See all <ArrowRight className="size-3.5" />
          </Link>
        </div>
        <div className="flex flex-wrap gap-2">
          {productTemplates.slice(0, 8).map((product) => (
            <Link
              key={product.slug}
              to={`/products/${product.slug}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border border-border bg-background text-foreground hover:border-primary/50 hover:text-primary hover:bg-primary/5 transition-colors"
            >
              {product.name}
            </Link>
          ))}
          <Link
            to="/products"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            +{productTemplates.length - 8} more
          </Link>
        </div>
      </div>

      {/* Trending this week */}
      {trendingSpecs.length > 0 && (
        <div className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="size-4 text-primary" />
            <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Trending this week
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {trendingSpecs.map((spec) => (
              <SpecCard
                key={spec.id}
                spec={spec}
                featureCount={spec.featureCount}
                complexityRange={spec.complexityRange}
                starCount={localStarCounts[spec.id] ?? spec.starCount ?? 0}
                isStarred={localStarred.has(spec.id)}
                onStar={handleStar}
                showCompare
              />
            ))}
          </div>
        </div>
      )}

      {/* Search + Sort */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search specs..."
          className="flex-1"
        />
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as SortOption)}
          className="px-3 py-2 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <option value="newest">Newest</option>
          <option value="starred">Most starred</option>
          <option value="alpha">Alphabetical</option>
        </select>
        <Button render={<Link to="/submit" />}>Submit a spec</Button>
      </div>

      {/* Category tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        <Link
          to="/"
          className={cn(
            'px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
            !activeCategory
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground hover:bg-muted/80',
          )}
        >
          All
        </Link>
        {categories.map((cat) => (
          <Link
            key={cat}
            to={`/?category=${cat}`}
            className={cn(
              'px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
              activeCategory === cat
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80',
            )}
          >
            {CATEGORY_LABELS[cat] ?? cat}
          </Link>
        ))}
      </div>

      {/* Feature filter chips */}
      {allFeatures.length > 0 && (
        <div className="mb-6">
          <p className="text-xs text-muted-foreground mb-2 font-medium">
            Filter by feature:
          </p>
          <div className="flex flex-wrap gap-1.5">
            {allFeatures.map((f) => {
              const active = selectedFeatures.includes(f.slug);
              return (
                <button
                  key={f.slug}
                  type="button"
                  onClick={() => toggleFeature(f.slug)}
                  className={cn(
                    'px-2.5 py-1 rounded-full text-xs font-medium border transition-colors',
                    active
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background text-muted-foreground border-border hover:border-primary/50 hover:text-primary',
                  )}
                >
                  {f.name}
                </button>
              );
            })}
            {selectedFeatures.length > 0 && (
              <button
                type="button"
                onClick={() => setSelectedFeatures([])}
                className="px-2.5 py-1 rounded-full text-xs font-medium text-muted-foreground hover:text-foreground"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>
      )}

      {/* Grid */}
      {filteredSpecs.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <p className="text-lg font-medium mb-1">No specs found</p>
          <p className="text-sm">Try a different search or category</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {visibleSpecs.map((spec) => (
              <SpecCard
                key={spec.id}
                spec={spec}
                featureCount={spec.featureCount}
                complexityRange={spec.complexityRange}
                starCount={localStarCounts[spec.id] ?? 0}
                isStarred={localStarred.has(spec.id)}
                onStar={handleStar}
                showCompare
              />
            ))}
          </div>
          {hasMore && (
            <div className="mt-8 flex justify-center">
              <Button
                variant="outline"
                onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
              >
                Load more
                <span className="ml-1.5 text-muted-foreground text-xs">
                  ({filteredSpecs.length - visibleCount} remaining)
                </span>
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
